import { Injectable, signal } from '@angular/core';

/**
 * Interface for storing original form values for change detection
 */
export interface OriginalFormValues {
  bookingValue: string | null;
  startDateValue: Date | null;
  endDateValue: Date | null;
  parkingPlaceValue: string | null;
  disabledParkingPlaceValue: boolean;
  paidValue: boolean;
  licensePlateNotKnownValue: boolean;
  licensePlateValue: string;
  licensePlateCountryCodeValue: string | null;
}

/**
 * Service to manage form state and change detection for parking request forms
 */
@Injectable()
export class ParkingRequestFormStateService {
  // Change detection signals
  hasChanges = signal<boolean>(false);

  // Store original values for change detection in edit mode
  private originalValues: OriginalFormValues | null = null;

  /**
   * Capture original values for change detection
   */
  captureOriginalValues(values: OriginalFormValues): void {
    this.originalValues = { ...values };
    this.hasChanges.set(false);
  }

  /**
   * Get original values
   */
  getOriginalValues(): OriginalFormValues | null {
    return this.originalValues;
  }

  /**
   * Detect if form values have changed from original values
   */
  detectChanges(currentValues: OriginalFormValues): void {
    if (!this.originalValues) {
      this.hasChanges.set(false);
      return;
    }

    const changed =
      currentValues.bookingValue !== this.originalValues.bookingValue ||
      !this.datesEqual(currentValues.startDateValue, this.originalValues.startDateValue) ||
      !this.datesEqual(currentValues.endDateValue, this.originalValues.endDateValue) ||
      currentValues.parkingPlaceValue !== this.originalValues.parkingPlaceValue ||
      currentValues.disabledParkingPlaceValue !== this.originalValues.disabledParkingPlaceValue ||
      currentValues.paidValue !== this.originalValues.paidValue ||
      currentValues.licensePlateNotKnownValue !== this.originalValues.licensePlateNotKnownValue ||
      currentValues.licensePlateValue !== this.originalValues.licensePlateValue ||
      currentValues.licensePlateCountryCodeValue !== this.originalValues.licensePlateCountryCodeValue;

    this.hasChanges.set(changed);
  }

  /**
   * Helper method to compare dates (handles null values)
   */
  private datesEqual(date1: Date | null, date2: Date | null): boolean {
    if (date1 === null && date2 === null) return true;
    if (date1 === null || date2 === null) return false;
    return date1.getTime() === date2.getTime();
  }

  /**
   * Reset change detection state
   */
  resetChangeDetection(): void {
    this.hasChanges.set(false);
    this.originalValues = null;
  }
}
